import React, { useState } from 'react';
import { styled, TextField, InputAdornment } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ModalComp from '../ModalComp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';

const CustomTextField = styled(TextField)(({ theme, paddingTop }) => ({
    '& .MuiOutlinedInput-root': {
        minHeight: '56px',
        '& fieldset': {
            borderRadius: '8px',
            borderColor: 'gray',
        },
        '&:hover fieldset': {
            borderColor: 'gray',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'gray',
        },
        paddingTop: paddingTop,
    },
    '& .MuiInputLabel-root': {
        color: 'gray',
        whiteSpace: 'break-spaces',
        width: 'max-content',
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%) scale(1)',
        transformOrigin: 'left',
        transition: 'all 0.2s ease-in-out',
        lineHeight: '1.5',
        pointerEvents: 'none',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'gray',
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiInputLabel-shrink': {
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiOutlinedInput-input': {
        padding: '16px',
        lineHeight: '1.5',
    },
    '& .MuiFormHelperText-root': {
        marginTop: '4px',
    },
}));

const CustomFileInput = ({ value, onChange, label, name, required, readOnly, disabled, ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filePreview, setFilePreview] = useState(value);
    const fileInputRef = React.createRef();

    const handleIconClick = () => {
        fileInputRef.current.click();
    };

    const allowedFileTypes = [
        'image/bmp',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'image/png',
        'application/pdf',
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/rtf', // .rtf
        'image/tiff', // .tif and .tiff
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if the file type is allowed
            if (!allowedFileTypes.includes(file.type)) {
                alert('Invalid file type. Please upload a valid file.');
                return;
            }
            setFilePreview({
                url: URL.createObjectURL(file),
                type: file.type,
            });
            onChange(event); // Pass the event back to the parent if needed
        }
    };

    // const handleFileRemove = (e) => {
    //     e.stopPropagation();
    //     setFilePreview(null);
    //     fileInputRef.current.value = null;
    //     onChange({ target: { name, value: '' } }); // Reset file input
    // };
    const handleFileRemove = (e) => {
        e.stopPropagation(); // Prevent triggering file selection
    
        // Clear the selected file
        setFilePreview(null);
        fileInputRef.current.value = ""; // Reset the input value
    
        // Trigger onChange event with an empty value if needed
        onChange({ target: { name, value: "" } });
    };


    const calculatePaddingTop = (label) => {
        const lineCount = label.split('\n').length;
        const basePadding = label.length >= 350 ? 50 : label.length > 200 ? 30 : '';
        const extraPadding = (lineCount - 1) * 10;
        return `${basePadding + extraPadding}px`;
    };

    const paddingTop = calculatePaddingTop(label);

    const closeModal = () => {
        setIsOpen(false);
    };

    const openModal = () => {
        setIsOpen(true);
    };

    // Function to check if the base64 string is a PDF
    const isPDF = (base64) => {
        return base64?.startsWith("JVBERi0") || base64?.startsWith("data:application/pdf;base64,");
    };

    return (
        <>
            <input

                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                name={name}
                readOnly={readOnly}
                {...props}
            />
            <div className='flex'>
                <CustomTextField
                    className='w-[100%] cursor-pointer'
                    label={label}
                    required={required}
                    {...props}

                    InputProps={{
                        style: {
                            color: 'black',
                            borderColor: 'gray',
                            cursor: 'pointer',
                        },
                        readOnly: readOnly,
                        endAdornment: (
                            <InputAdornment position="end" style={{ cursor: 'pointer', height: '100%', alignItems: 'center' }}>
                                <div className='flex gap-[10px] items-center '>
                                    <AttachFileIcon className='text-3xl text-[#004878]' />
                                    {filePreview || value ? (
                                        <>
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openModal();
                                                }}
                                            >
                                                <VisibilityIcon className='text-3xl text-[#004878]' />
                                            </div>
                                            <ClearIcon className='text-3xl text-red-600 cursor-pointer' onClick={handleFileRemove} />

                                        </>
                                    ) : ''}
                                </div>
                            </InputAdornment>
                        ),
                    }}
                    id="outlined-file-input"
                    variant="outlined"
                    value={value ? value.slice(0, 200) : ''}
                    onClick={handleIconClick}
                    paddingTop={paddingTop}
                    disabled={disabled}
                />



                <ModalComp closeModal={closeModal} padding='0px' modalIsOpen={isOpen} >
                    <div className={`${(filePreview?.type === 'application/pdf') || isPDF(value) ? 'w-[100%]' : 'max-w-[500px]'} items-center flex justify-center min-h-[200px] max-h-[400px] relative  `}>
                        <div className='w-[100%] flex justify-start p-[7px] top-0 absolute z-10'>
                            <div onClick={closeModal} className='bg-white cursor-pointer rounded-full p-[2px] ' >
                                <ClearIcon className='text-black' />
                            </div>
                        </div>
                        {filePreview?.url || value ? (
                            filePreview?.type === 'application/pdf' || isPDF(value) ? (
                                <iframe
                                    src={filePreview?.url || `data:application/pdf;base64,${value}`}
                                    style={{ width: '100%', height: '500px' }}
                                    title="PDF Preview"
                                />
                            ) : filePreview?.url || value ? (
                                <img
                                    className='object-cover bg-fixed'
                                    src={filePreview?.url ? filePreview?.url : `data:image/jpeg;base64,${value}`}
                                    alt="Selected file"
                                />
                            ) : (
                                <p>No file selected</p>
                            )
                        ) : (
                            <p>No file selected</p>
                        )}
                    </div>
                </ModalComp>
            </div>
        </>
    );
};

export default CustomFileInput;
